import type { ServiceDiagnosticsResponse } from '@web/vin-sessions/types';

import { useAuthState } from '@packages/contexts/auth';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { Button, Icon, Typography } from '@packages/ui/shared';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { docsService } from '@web/services/singletons';
import {
    isPopupGuidanceDismissed,
    persistPopupGuidanceDismissed,
    PopupGuidanceBanner,
} from '@web/vin-sessions/components/stis-publications/popup-guidance-banner';
import { PopupGuidanceHelpPill } from '@web/vin-sessions/components/stis-publications/popup-guidance-help-pill';
import { PublicationType } from '@web/vin-sessions/components/stis-publications/publication-type-icons';
import { fetchCdnDocumentUrlByDocId, openDocumentTab } from '@web/vin-sessions/utils/open-document-tab';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { useNavigate } from 'react-router-dom';

import { SearchManuals } from './search-manuals';

const logger = getLogger('service-diagnostics');

/** Model years shown before "View Full Archives" is used. */
const DEFAULT_YEARS_COUNT = 6;

const ARCHIVES_TRANSITION_MS = 320;

const TROUBLESHOOTING_SEARCH_PATH = '/service-diagnostics/troubleshooting-search';

const SKELETON_CARD_KEYS = Array.from({ length: DEFAULT_YEARS_COUNT }, (_, index) => `skeleton-card-${index}`);

const SKELETON_ROW_KEYS = Array.from({ length: 5 }, (_, index) => `skeleton-row-${index}`);

const styles = StyleSheet.create({
    pageTitle: {
        lineHeight: 40,
    },
});

const useStyles = createUseStyles({
    /** Same bar as the SSM5 Diagnostic page header. */
    pageHeader: {
        alignItems: 'center',
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        gap: 16,
        justifyContent: 'space-between',
        minHeight: 72,
        padding: '16px 24px 15px',
    },
    container: {
        padding: 24,
    },
    /** Browse and Search are two views of one page, so the switch sits beside the title. */
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        minWidth: 0,
    },
    modeToggle: {
        display: 'inline-flex',
        padding: 3,
        backgroundColor: colors.grayOne,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 6,
    },
    modeButton: {
        display: 'flex',
        alignItems: 'center',
        height: 34,
        padding: '0 18px',
        border: 'none',
        borderRadius: 4,
        backgroundColor: 'transparent',
        color: colors.textDarkSecondary,
        fontSize: 15,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: 2,
        },
    },
    modeButtonActive: {
        backgroundColor: colors.white,
        color: colors.blueOne,
        boxShadow: '0 1px 2px rgba(7, 20, 36, 0.12)',
    },
    helpPillRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: 16,
    },
    yearGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 20,
        '@media (max-width: 1400px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        '@media (max-width: 900px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    yearCard: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 8,
        overflow: 'hidden',
    },
    yearCardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        borderBottom: `1px solid ${colors.grayThree}`,
    },
    yearLabel: {
        fontSize: 19,
        fontWeight: 700,
        lineHeight: '24px',
        color: colors.textDarkPrimary,
    },
    yearCount: {
        padding: '3px 10px',
        borderRadius: 100,
        backgroundColor: colors.blueFour,
        color: colors.blueOne,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    carlineRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minHeight: 44,
        padding: '0 16px',
        border: 'none',
        borderBottom: `1px solid ${colors.grayTwo}`,
        backgroundColor: colors.white,
        color: colors.blueLink,
        fontSize: 15,
        textAlign: 'left',
        cursor: 'pointer',
        '&:last-child': {
            borderBottom: 'none',
        },
        '&:hover': {
            backgroundColor: colors.grayOne,
            color: colors.blueLinkHover,
        },
        '&:focus-visible': {
            outline: `2px solid ${colors.blueOne}`,
            outlineOffset: -2,
        },
    },
    skeletonBar: {
        marginBottom: 0,
    },
    skeletonRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 44,
        padding: '0 16px',
        borderBottom: `1px solid ${colors.grayTwo}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    emptyState: {
        padding: '48px 24px',
        textAlign: 'center',
        color: colors.textDarkSecondary,
        fontSize: 15,
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 8,
    },
    /**
     * Pinned to the bottom of the scrolling pane so the archives button is reachable on
     * landing, without hiding the last row of carlines behind an opaque bar.
     */
    archivesBar: {
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        marginTop: 24,
        paddingTop: 48,
        paddingBottom: 20,
        /*
         * Deliberately captures clicks. Letting them through meant a click on the blurred
         * strip opened whichever carline happened to sit underneath it.
         */
        pointerEvents: 'auto',
    },
    /** The fade is dropped once the end of the list is on screen. */
    archivesBarFloating: {
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: 132,
            background:
                'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.92) 55%, #fff 100%)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            pointerEvents: 'none',
        },
    },
    barButtons: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    bottomSentinel: {
        height: 1,
    },
    /**
     * Animates to the content's real height. A max-height guess is what makes this kind of
     * collapse look jerky, because the guess never matches the actual card heights.
     */
    archivesWrapper: {
        display: 'grid',
        gridTemplateRows: '0fr',
        transition: `grid-template-rows ${ARCHIVES_TRANSITION_MS}ms ease`,
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
        },
    },
    archivesWrapperOpen: {
        gridTemplateRows: '1fr',
    },
    archivesInner: {
        minHeight: 0,
        overflow: 'hidden',
    },
    archivesInnerClosed: {
        pointerEvents: 'none',
        visibility: 'hidden',
        transition: `visibility 0s linear ${ARCHIVES_TRANSITION_MS}ms`,
    },
    archivesGrid: {
        paddingTop: 20,
    },
});

export const ServiceDiagnostics: FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const navigate = useNavigate();
    const { token, account } = useAuthState();

    const [serviceDiagnostics, setServiceDiagnostics] = useState<ServiceDiagnosticsResponse>({});
    const [isLoading, setIsLoading] = useState(true);
    const [showAllYears, setShowAllYears] = useState(false);
    const [isEndOfListVisible, setIsEndOfListVisible] = useState(false);
    const endOfListObserverRef = useRef<IntersectionObserver | null>(null);

    const [guidanceDismissed, setGuidanceDismissed] = useState<boolean>(isPopupGuidanceDismissed);
    const [guidanceReopened, setGuidanceReopened] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);
    const { isServiceDiagnosticsSearchFeatureEnabled } = useFeatureFlags();
    const [selectedMode, setMode] = useState<'browse' | 'search'>('browse');
    // Derived, not stored: the flag can be switched off while someone is sitting on the page.
    const mode = isServiceDiagnosticsSearchFeatureEnabled ? selectedMode : 'browse';
    const showGuidance = popupBlocked || guidanceReopened || !guidanceDismissed;

    const canSearchTroubleshootingSessions =
        Boolean(
            (account?.docsCapabilities as Record<string, unknown> | undefined)?.['canSearchTroubleShootingSessions']
        ) || account?.docsCapabilities?.canSearchAllTroubleShootingSessions === true;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await docsService.getAllServiceDiagnostics();
                if (res && res.success) {
                    setServiceDiagnostics(res.data);
                }
            } catch (err) {
                logger.error('Failed to fetch the service diagnostics', { error: err });
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const sortedYears = useMemo(
        () => Object.keys(serviceDiagnostics).sort((a, b) => Number(b) - Number(a)),
        [serviceDiagnostics]
    );

    const carlinesByYear = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(serviceDiagnostics).map(([year, carlines]) => [
                    year,
                    [...carlines].sort((a, b) =>
                        a.carlineDescription.localeCompare(b.carlineDescription, undefined, {
                            numeric: true,
                            sensitivity: 'base',
                        })
                    ),
                ])
            ),
        [serviceDiagnostics]
    );

    const hasArchives = sortedYears.length > DEFAULT_YEARS_COUNT;

    const recentYears = useMemo(() => sortedYears.slice(0, DEFAULT_YEARS_COUNT), [sortedYears]);
    const archiveYears = useMemo(() => sortedYears.slice(DEFAULT_YEARS_COUNT), [sortedYears]);

    const handleGuidanceClose = useCallback(() => {
        persistPopupGuidanceDismissed();
        setGuidanceDismissed(true);
        setGuidanceReopened(false);
        setPopupBlocked(false);
    }, []);

    const handleToggleArchives = useCallback(() => {
        setShowAllYears((previous) => !previous);
    }, []);

    // The bar keeps its fade until the end of the list is on screen, at which point it is
    // sitting at its natural position and there is nothing left underneath to hint at.
    const setBottomSentinel = useCallback((node: HTMLDivElement | null) => {
        endOfListObserverRef.current?.disconnect();
        if (!node) {
            setIsEndOfListVisible(false);
            return;
        }
        endOfListObserverRef.current = new IntersectionObserver((entries) =>
            setIsEndOfListVisible(entries[0].isIntersecting)
        );
        endOfListObserverRef.current.observe(node);
    }, []);

    const handleOpenDocument = useCallback(
        async (documentId: string) => {
            await openDocumentTab(
                () => fetchCdnDocumentUrlByDocId(documentId, token),
                () => setPopupBlocked(true)
            );
        },
        [token]
    );

    const searchSessionsLabel = t(
        'stisPublications:serviceDiagnostics.searchTroubleshootingSessions',
        'Search Troubleshooting Sessions'
    );

    const renderYearCard = (year: string) => {
        const carlines = carlinesByYear[year] ?? [];
        return (
            <div className={classes.yearCard} key={year}>
                <div className={classes.yearCardHeader}>
                    <span className={classes.yearLabel}>{year}</span>
                    <span className={classes.yearCount}>
                        {t('stisPublications:serviceDiagnostics.manualCount', { count: carlines.length })}
                    </span>
                </div>
                {carlines.map((diagnostic) => (
                    <button
                        className={classes.carlineRow}
                        key={diagnostic.carlineId}
                        onClick={() => handleOpenDocument(diagnostic.documentId)}
                        type="button"
                    >
                        <PublicationType publicationType="menuBook" size={24} />
                        <span>{diagnostic.carlineDescription}</span>
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div>
            <header className={classes.pageHeader}>
                <div className={classes.headerLeft}>
                    <Typography style={styles.pageTitle} variant="h2">
                        {t('stisPublications:breadcrumbs.serviceDiagnostics', 'Service Diagnostics')}
                    </Typography>
                    {isServiceDiagnosticsSearchFeatureEnabled && (
                        <div className={classes.modeToggle} role="tablist">
                            <button
                                aria-selected={mode === 'browse'}
                                className={`${classes.modeButton} ${mode === 'browse' ? classes.modeButtonActive : ''}`}
                                onClick={() => setMode('browse')}
                                role="tab"
                                type="button"
                            >
                                {t('stisPublications:serviceDiagnostics.browseByYear', 'Browse by Year')}
                            </button>
                            <button
                                aria-selected={mode === 'search'}
                                className={`${classes.modeButton} ${mode === 'search' ? classes.modeButtonActive : ''}`}
                                onClick={() => setMode('search')}
                                role="tab"
                                type="button"
                            >
                                {t('stisPublications:serviceDiagnostics.searchManuals', 'Search Manuals')}
                            </button>
                        </div>
                    )}
                </div>
            </header>
            <div className={classes.container}>
                {/* In Search Manuals the banner sits under the tabs, so the tabs stay at the top. */}
                {mode === 'browse' && (
                    <PopupGuidanceBanner
                        blocked={popupBlocked}
                        onClose={handleGuidanceClose}
                        show={showGuidance}
                        style={{ marginBottom: 24 }}
                    />
                )}
                {!showGuidance && mode === 'browse' && (
                    <div className={classes.helpPillRow}>
                        <PopupGuidanceHelpPill onPress={() => setGuidanceReopened(true)} />
                    </div>
                )}

                {mode === 'search' ? (
                    <SearchManuals
                        diagnostics={serviceDiagnostics}
                        guidanceBanner={
                            <PopupGuidanceBanner
                                blocked={popupBlocked}
                                onClose={handleGuidanceClose}
                                show={showGuidance}
                                style={{ marginBottom: 0 }}
                            />
                        }
                        helpPill={
                            showGuidance ? undefined : (
                                <PopupGuidanceHelpPill onPress={() => setGuidanceReopened(true)} />
                            )
                        }
                        onPopupBlocked={() => setPopupBlocked(true)}
                    />
                ) : isLoading ? (
                    <div
                        aria-busy
                        aria-label={t('stisPublications:serviceDiagnostics.loading', 'Loading Service Diagnostics')}
                        className={classes.yearGrid}
                        role="status"
                    >
                        {SKELETON_CARD_KEYS.map((cardKey) => (
                            <div className={classes.yearCard} key={cardKey}>
                                <div className={classes.yearCardHeader}>
                                    <SkeletonLoader
                                        className={classes.skeletonBar}
                                        height={18}
                                        type="text"
                                        width={64}
                                    />
                                    <SkeletonLoader className={classes.skeletonBar} type="badge" width={82} />
                                </div>
                                {SKELETON_ROW_KEYS.map((rowKey) => (
                                    <div className={classes.skeletonRow} key={`${cardKey}-${rowKey}`}>
                                        <SkeletonLoader className={classes.skeletonBar} type="icon" />
                                        <SkeletonLoader className={classes.skeletonBar} type="text" width="55%" />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : sortedYears.length === 0 ? (
                    <div className={classes.emptyState}>{t('stisPublications:serviceDiagnostics.empty')}</div>
                ) : (
                    <>
                        <div className={classes.yearGrid}>{recentYears.map(renderYearCard)}</div>

                        {archiveYears.length > 0 && (
                            <div
                                aria-hidden={!showAllYears}
                                className={`${classes.archivesWrapper} ${
                                    showAllYears ? classes.archivesWrapperOpen : ''
                                }`}
                            >
                                <div
                                    className={`${classes.archivesInner} ${
                                        showAllYears ? '' : classes.archivesInnerClosed
                                    }`}
                                >
                                    <div className={`${classes.yearGrid} ${classes.archivesGrid}`}>
                                        {archiveYears.map(renderYearCard)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}

                {!isLoading && (mode === 'browse' ? canSearchTroubleshootingSessions || hasArchives : false) && (
                    <>
                        <div
                            className={`${classes.archivesBar} ${
                                isEndOfListVisible ? '' : classes.archivesBarFloating
                            }`}
                        >
                            <div className={classes.barButtons}>
                                {canSearchTroubleshootingSessions && (
                                    <Button
                                        accessibilityLabel={searchSessionsLabel}
                                        accessibilityRole="button"
                                        onPress={() => navigate(TROUBLESHOOTING_SEARCH_PATH)}
                                        title={searchSessionsLabel}
                                        variant="primary"
                                    />
                                )}
                                {hasArchives && (
                                    <Button
                                        iconRight={
                                            <Icon
                                                color="white"
                                                name={showAllYears ? 'chevron-up' : 'chevron-down'}
                                                size={20}
                                            />
                                        }
                                        onPress={handleToggleArchives}
                                        title={
                                            showAllYears
                                                ? t(
                                                      'stisPublications:serviceDiagnostics.hideArchives',
                                                      'Hide Full Archives'
                                                  )
                                                : t(
                                                      'stisPublications:serviceDiagnostics.viewArchives',
                                                      'View Full Archives'
                                                  )
                                        }
                                        variant="secondary"
                                    />
                                )}
                            </div>
                        </div>
                        <div className={classes.bottomSentinel} ref={setBottomSentinel} />
                    </>
                )}
            </div>
        </div>
    );
};
